import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import './UserCollectionForm.scss';

import {
	Field, FieldPair, Form, Input, Label, SubmitButton, ValidationMessage, CheckboxField
} from 'components/Form';
import { buildSchema } from 'features/formValidation';

const validationCopy = {
	required: 'This field is required.',
	email: 'Please enter a valid email address.',
	maxChar: (max) => `Please enter no more than ${max} characters.`
};

const UserCollectionForm = ({
	title, topCopy, bottomCopy, onSubmit, source, skipValidation = false
}) => {

	const handleSubmit = (data) => {

		const formData = { ...data };

		formData.firstName = formData.firstName.trim();
		formData.lastName = formData.lastName.trim();
		formData.emailAddress = formData.emailAddress.trim();

		// Submit prop is not necessary for submission of the form
		if (typeof formData.Submit !== 'undefined') {

			delete formData.Submit;

		}

		onSubmit(formData);

	};

	const validationSchema = useMemo(() => {

		// minimum validation for email format if skipValidation prop is passed
		if (skipValidation) {

			// only validate email if entered
			return buildSchema((yup) => ({
				emailAddress: yup
					.string()
					.email(validationCopy.email)
					.max(50, ({ max }) => validationCopy.maxChar(max))
			}));

		}

		return buildSchema((yup) => ({
			firstName: yup
				.string()
				.required(validationCopy.required)
				.trim()
				.lettersonly()
				.max(50, ({ max }) => validationCopy.maxChar(max)),
			lastName: yup
				.string()
				.required(validationCopy.required)
				.trim()
				.lettersonly()
				.max(50, ({ max }) => validationCopy.maxChar(max)),
			emailAddress: yup
				.string()
				.required(validationCopy.required)
				.email(validationCopy.email)
				.max(50, ({ max }) => validationCopy.maxChar(max))
		}));

	}, [ skipValidation ]);

	return (
		<div className="UserCollectionForm">
			<div className="UserCollectionForm__title h1">
				{title}
			</div>
			<p className="UserCollectionForm__topCopy">
				{topCopy}
			</p>
			<p className="UserCollectionForm__requiredLabel">* Required</p>
			<Form
				onSubmit={handleSubmit}
				validationSchema={validationSchema}
				defaultValues={{
					emailOptIn: true
				}}
				noValidate
			>
				<FieldPair>
					<Field>
						<Label htmlFor="UserCollectionFormFirstName" className="UserCollectionForm__label" required>
							First Name
						</Label>
						<Input
							id="UserCollectionFormFirstName"
							name="firstName"
							type="text"
							placeholder="First Name"
							className="UserCollectionForm__input"
						/>
						<ValidationMessage errorsFor="firstName" />
					</Field>
					<Field>
						<Label htmlFor="UserCollectionFormLastName" className="UserCollectionForm__label" required>
							Last Name
						</Label>
						<Input
							id="UserCollectionFormLastName"
							name="lastName"
							type="text"
							placeholder="Last Name"
							className="UserCollectionForm__input"
						/>
						<ValidationMessage errorsFor="lastName" />
					</Field>
				</FieldPair>
				<Field>
					<Label htmlFor="UserCollectionFormEmailAddress" className="UserCollectionForm__label" required>
						Email Address
					</Label>
					<Input
						id="UserCollectionFormEmailAddress"
						name="emailAddress"
						type="email"
						placeholder="Email Address"
						className="UserCollectionForm__input"
					/>
					<ValidationMessage errorsFor="emailAddress" />
				</Field>
				<Field className="UserCollectionForm__hiddenField">
					<Input id="UserCollectionFormSource" name="source" type="hidden" value={source} />
				</Field>
				<CheckboxField name="emailOptIn" id="emailOptIn" value="">
					Email me exclusive offers, sale alerts, &amp; more
				</CheckboxField>
				<p className="UserCollectionForm__bottomCopy">
					{bottomCopy}
				</p>
				<p className="UserCollectionForm__bottomNote">
					Note: We will only display your first name and last initial.
				</p>
				<Field>
					<SubmitButton name="Submit" buttonStyleVariant="secondary" size="large" />
				</Field>
			</Form>
		</div>
	);

};

UserCollectionForm.propTypes = {
	title: PropTypes.string.isRequired,
	topCopy: PropTypes.string.isRequired,
	bottomCopy: PropTypes.string,
	onSubmit: PropTypes.func.isRequired,
	skipValidation: PropTypes.bool,
	source: PropTypes.string.isRequired
};

export default UserCollectionForm;
